import {useState, useCallback} from "react";
import {useGraphqlLazyQuery} from "../../../../amplify/hooks/useGraphqlLazyQuery";
import {tenantById as tenantByIdQuery} from "../../../../amplify/graphql/queries";
import type {TenantByIdQuery, TenantByIdQueryVariables} from "../../../../amplify/graphql/API";

interface ValidatedTenant {
  id: string;
  name: string;
  synthetic: boolean;
  environment: string | null;
}

interface TenantValidationState {
  isValidating: boolean;
  validatedTenant: ValidatedTenant | null;
  error: string | null;
}

/**
 * Custom hook for validating tenant IDs on blur.
 * Queries the BFF tenantById endpoint to verify the tenant exists
 * and retrieve its properties (name, synthetic, environment).
 */
export function useTenantValidation(): TenantValidationState & {
  validateTenantId: (tenantId: string) => Promise<void>;
  clearValidation: () => void;
} {
  const [state, setState] = useState<TenantValidationState>({
    isValidating: false,
    validatedTenant: null,
    error: null
  });

  const [fetchTenant] = useGraphqlLazyQuery<TenantByIdQuery, TenantByIdQueryVariables>({
    amplifyQuery: tenantByIdQuery
  });

  const validateTenantId = useCallback(async (tenantId: string) => {
    if (!tenantId.trim()) {
      setState({isValidating: false, validatedTenant: null, error: null});
      return;
    }

    setState({isValidating: true, validatedTenant: null, error: null});

    try {
      const result = await fetchTenant({variables: {id: tenantId.trim()}});
      const tenant = result.data?.tenantById;

      if (!tenant) {
        setState({
          isValidating: false,
          validatedTenant: null,
          error: "Tenant not found"
        });
        return;
      }

      const synthetic = tenant.synthetic ?? false;

      if (!synthetic) {
        setState({
          isValidating: false,
          validatedTenant: null,
          error: "Tenant is not synthetic. Only synthetic tenants can be used for self-service entity creation."
        });
        return;
      }

      setState({
        isValidating: false,
        validatedTenant: {
          id: tenant.id,
          name: tenant.name,
          synthetic,
          environment: tenant.environment ?? null
        },
        error: null
      });
    } catch {
      setState({
        isValidating: false,
        validatedTenant: null,
        error: "Failed to validate tenant"
      });
    }
  }, [fetchTenant]);

  const clearValidation = useCallback(() => {
    setState({isValidating: false, validatedTenant: null, error: null});
  }, []);

  return {
    ...state,
    validateTenantId,
    clearValidation
  };
}
